/**
 * @fileoverview Provides advanced single-line code autocomplete using AI. Leverages language server context, recent changes, and a language model for intelligent code suggestions. Focuses on accuracy and conciseness for single-line edits.
 */

import Diff = require('diff')
import { dedent, minIndentationAmount } from '@iterator/shared-common/src/dedent'
import { Message, Model } from '@iterator/shared-node/ai/llm_types'
import {
  diff_block,
  error,
  info,
  info_block,
  print_ai,
  print_ai_stream,
} from '@iterator/shared-node/util/logging'
import { Llama_33_70B_n1_singleLine_Model } from '@src/ai/models'
import { Block } from '@src/ast'
import { parsePossibleCodeBlock } from '@src/code_block'
import { CodeError, LanguageServer } from '@src/context_ts_language'
import { getTokenCount } from '@src/folder'
import { isDiffWithOnlyOneAdditionOrEdit, pickCursorFromLineDiff } from '@src/position'
import { requireSettings } from '@src/settings'
import { trace, trackLlmUsage } from '../ai/llm_logging'
import { AiStreamer } from '../ai_streamer'
import { OnDiffReceiver, PathDiff, printOnDiff } from '../on_diff'
import { changesToString, truncateDiffChanges } from '../rewrite'
import { CommentDiffSerializer, DiffTracker } from './diff_tracker'

const CURSOR_PLACEHOLDER = '<|cursor|>'

/**
 * V8.0
 * Things from V5.0:
 * - replace getBlock with bestFitStatementsOrBlock for tighter context (200 char limit)
 * - speculative decoding (when model allows)
 * - recent changes as diffs
 * Things from V6.0:
 * - prompt updates
 * - context provided: surrounding_code_block, type_definitions, change_history, most_recent_diff
 * - allow pasting "<|cursor|>" to indicate the cursor position and trigger autocomplete
 * Things from V7.0:
 * - include cursor location in the prompt
 * - complete the line when no block is viable
 * - prompt including types of cases to consider
 * - use comments for diff
 * - cost and usage breakdown
 * Things from V8.0:
 * - coalesce overlapping diffs based on file
 * - language server to find errors in the block
 * - [explored but not used] use jaccard similarity over recent files to replace surrounding context
 *
 * New for V9:
 * - single line with fireworksai multi-result and stop
 * - prompt engineering for this case
 * - oin <most_recent_diff>, add more lines of context and highlight the line being changed
 *
 * V9.1
 * - add more examples of what to fix
 *
 * Notes:
 * - Fireworksai is much faster that openai and anthropic (~1s with Llama3.3-70B)
 * - n of 4 often just returns the same result. and you can use that plus speculative decoding to get a single result
 * - Llama3.3:
 *  -- comments are often more verbose.
 *  -- i messed up the prompt, it was including ALL the context and system at first
 * - block is better for fixing whitespace changes
 * - autocompleting <1s while im typing is very cool but uses a lot of tokens
 **/
export class AutocompleteV9 implements OnDiffReceiver {
  mostRecentResult: string | undefined = undefined
  private isGenerating = false

  private model: Model = Llama_33_70B_n1_singleLine_Model
  // private model: Model = Llama_33_70B_Groq_Model

  constructor(
    private languageServer: LanguageServer,
    private quiet: boolean = false,
    private diffTracker: DiffTracker = new DiffTracker(CommentDiffSerializer),
    private onSuggestion: (() => Promise<void>) | undefined = undefined,
  ) {
    requireSettings([...this.model.getApi().requiredKeys])
  }

  async onDiff(diff: PathDiff) {
    const isCursorDiff = cursorPlaceholderDiff(diff.changes)

    if (isCursorDiff === CursorPlaceholderDiff.NonCursorDiff) {
      this.diffTracker.onDiff(diff)
    } else {
      info('👀 Cursor diff detected')
    }

    if (this.isGenerating) {
      return
    }
    this.isGenerating = true

    try {
      printOnDiff(diff)

      if (
        !isDiffWithOnlyOneAdditionOrEdit(diff.changes) ||
        isCursorDiff === CursorPlaceholderDiff.Removed
      ) {
        if (!this.quiet) {
          info(
            `👎 No addition or edit to complete ${isCursorDiff === CursorPlaceholderDiff.Removed ? '(cursor removed)' : ''}`,
          )
        }
        return
      }

      await this.doAutocomplete(isCursorDiff, diff)
    } catch (e: any) {
      error(e.stack || e.message || e)
    } finally {
      this.isGenerating = false
    }
  }

  private async doAutocomplete(isCursorDiff: CursorPlaceholderDiff, diff: PathDiff) {
    await trace(
      async () => {
        const startTime = Date.now()
        const originalContents =
          isCursorDiff === CursorPlaceholderDiff.Added ? diff.before : diff.after

        const cursorPosition = pickCursorFromLineDiff(diff.changes)
        if (!cursorPosition) {
          info('👎 No cursor position provided')
          return
        }

        if (!this.quiet) {
          info_block('cursorPosition', cursorPosition)
        }

        let fileContents = originalContents
        const blockToRewrite = lineToBlock(originalContents, cursorPosition.row)
        if (blockToRewrite.text.trim().length > 0) {
          fileContents = await this.askModelForRewrite(
            diff.path,
            originalContents,
            insertCursorIntoCodeBlock(blockToRewrite, cursorPosition),
            this.model,
            this.quiet,
          )
        } else {
          info('👻 No valid block and line is empty')
        }
        if (this.onSuggestion) {
          await this.onSuggestion()
        }
        diff_block(
          `Proposed changes (${Date.now() - startTime}ms)`,
          originalContents !== fileContents
            ? changesToString(
                truncateDiffChanges(Diff.diffLines(originalContents, fileContents), 5),
              )
            : 'No changes',
        )
        this.mostRecentResult = fileContents
      },
      {
        name: 'AutocompleteV9' + this.model.name,
      },
    )
  }

  private async askModelForRewrite(
    filePath: string,
    fileContents: string,
    blockToRewrite: { start: number; end: number; text: string },
    model: Model,
    quiet: boolean,
  ): Promise<string> {
    info('📝 Asking model for rewrite')
    const changeHistory = this.diffTracker.getRecentChangesTexts(4)
    const codeBlockToRewrite = blockToRewrite.text

    const startGettingDefinitions = Date.now()
    const definitions = (
      await this.languageServer.getDefinitionsInRange(
        filePath,
        blockToRewrite.start,
        blockToRewrite.end,
      )
    ).filter((d: string) => !codeBlockToRewrite.includes(d))
    const endGettingDefinitions = Date.now()
    const errors = await this.languageServer.getErrorsInRange(
      filePath,
      blockToRewrite.start,
      blockToRewrite.end,
    )

    const definitionsMessage = `<type_definitions>\n${definitions.join('\n\n')}\n</type_definitions>`
    const changeHistoryMessage = `<recent_changes>\n${changeHistory.join('\n\n')}\n</recent_changes>`
    const mostRecentDiff = this.diffTracker.mostRecentDiffText()
    const mostRecentDiffMessage = mostRecentDiff
      ? `<change_to_complete>\n${mostRecentDiff}\n</change_to_complete>`
      : ''
    const codeErrorsMessage = `<code_errors>\n${serializeErrors(errors)}\n</code_errors>`
    const codeBlockToRewriteText = `<line_to_rewrite>\n${codeBlockToRewrite}\n</line_to_rewrite>`

    if (!quiet) {
      info_block(
        `definitions ${endGettingDefinitions - startGettingDefinitions}ms`,
        definitionsMessage,
      )
      info_block('changeHistory', changeHistoryMessage)
      info_block('mostRecentDiff', mostRecentDiffMessage)
      info_block('codeErrors', codeErrorsMessage)
      info_block('blockToRewrite', codeBlockToRewriteText)
    }

    const streamer = new AiStreamer(print_ai_stream, print_ai_stream)
    return trace(async (span) => {
      const system = `You are an expert coder who is accurate, thorough and concise.
You are provided with a line of code to improve in <line_to_rewrite> based on <recent_changes>.
<recent_changes> contains the recent changes. Your change should be based on these changes.
<change_to_complete> contains the line and the surrounding context you can use to improve the line.
You do not repeat surrounding context in your output.

Look for changes such as:
- Add missing arguments to a method call
- Add missing types to a variable declaration
- Add missing parameters to a constructor
- Complete a condition in an if statement
- Fill in array/object destructuring
- Complete a promise chain method
- Complete template string expressions
- Add object properties in a declaration
- Convert between common syntax patterns (for loop ↔ array method)
- Fill in generic type parameters
- Complete optional chaining expressions
- Complete a function return statement
- Add missing event handler parameters
- Fill in regex pattern
- Complete enum values
- Fill in array/object spread operations
- Complete default parameter values
- Fill in operator precedence with parentheses`
      const texts = []
      if (definitions.length > 0) {
        texts.push(definitionsMessage)
      }
      if (errors.length > 0) {
        texts.push(codeErrorsMessage)
      }
      if (changeHistory.length > 0) {
        texts.push(changeHistoryMessage)
      }
      if (mostRecentDiffMessage) {
        texts.push(mostRecentDiffMessage)
      }
      const messages: Message[] = [
        {
          role: 'user',
          content: `Rewrite the line and improve it. Output nothing else. Your output should not include a markdown code block like \`\`\`.`,
        },
        {
          role: 'user',
          content: codeBlockToRewriteText,
        },
      ]
      const { message, usage } = await model.getApi().streamChatCompletions(
        system,
        texts,
        messages,
        (chunk) => {
          if (!quiet) {
            streamer.stream(chunk)
          }
        },
        codeBlockToRewrite,
      )

      if (!quiet) {
        print_ai('')
      }

      trackLlmUsage(
        span,
        messages,
        message.content,
        usage.promptTokens,
        usage.completionTokens,
        model,
      )

      if (!quiet) {
        info_block(`Usage ${model.name}`, {
          ...usage,
          promptMessages: messages.map((m) => getTokenCount(m.content)).reduce((a, b) => a + b, 0),
          codeBlock: getTokenCount(codeBlockToRewriteText),
          mostRecentDiff: mostRecentDiff ? getTokenCount(mostRecentDiffMessage) : 0,
          definitions: getTokenCount(definitionsMessage),
          changeHistory: getTokenCount(changeHistoryMessage),
          promptCost: usage.promptTokens * model.promptTokenCost,
          completionCost: usage.completionTokens * model.completionTokenCost,
          cost:
            usage.promptTokens * model.promptTokenCost +
            usage.completionTokens * model.completionTokenCost,
        })
      }

      if (message.content === 'no change') {
        return fileContents
      }

      return fillInLineNumberMatchingOriginalWhiteSpace(
        fileContents,
        blockToRewrite.start + 1,
        blockToRewrite.end + 1,
        parsePossibleCodeBlock(message.content),
      )
    })
  }
}

enum CursorPlaceholderDiff {
  Added,
  Removed,
  NonCursorDiff,
}
// Detect if this diff includes adding or removing the cursor placeholder
function cursorPlaceholderDiff(diff: Diff.Change[]): CursorPlaceholderDiff {
  for (const change of diff) {
    if ((change.added || change.removed) && change.value.includes(CURSOR_PLACEHOLDER)) {
      return change.added ? CursorPlaceholderDiff.Added : CursorPlaceholderDiff.Removed
    }
  }
  return CursorPlaceholderDiff.NonCursorDiff
}

function fillInLineNumberMatchingOriginalWhiteSpace(
  content: string,
  startLine: number,
  endLine: number,
  replacement: string,
): string {
  const lines = content.split('\n')
  const minIndentation = minIndentationAmount(lines.slice(startLine - 1, endLine))
  const indentedReplacementLines = replacement.split('\n').map((line) => {
    if (line.length === 0) {
      return ''
    }
    return ' '.repeat(minIndentation) + line
  })

  return [
    ...lines.slice(0, startLine - 1),
    ...indentedReplacementLines,
    ...lines.slice(endLine),
  ].join('\n')
}

function insertCursorIntoCodeBlock(
  codeBlock: { text: string; start: number; end: number; indentedText: string },
  position: { row: number; column: number },
): { text: string; start: number; end: number } {
  const lines = codeBlock.indentedText.split('\n')
  const oneIndexedRow = position.row + 1 // lol this is annoying
  const lineToEdit = lines[oneIndexedRow - codeBlock.start]
  if (!lineToEdit) {
    return codeBlock
  }

  lines[oneIndexedRow - codeBlock.start] =
    lineToEdit.slice(0, position.column) + CURSOR_PLACEHOLDER + lineToEdit.slice(position.column)
  return {
    text: dedent(lines.join('\n')),
    start: codeBlock.start,
    end: codeBlock.end,
  }
}

/**
 * Convert a single line of text into a block.
 */
function lineToBlock(text: string, position: number): Block {
  const lineText = text.split('\n')[position]!
  return {
    text: dedent(lineText),
    start: position,
    end: position,
    before: [],
    current: [lineText],
    after: [],
    indentedText: lineText,
  }
}

function serializeErrors(errors: CodeError[]): string {
  return errors.map((e) => `${e.message} at ${e.start.row}:${e.start.col}`).join('\n')
}
